<template>
  <div
    class="dashboard-banner-slider"
    :class="{ 'is-jumping': !isTransitioning }">
    <div class="dashboard-banner-slider__main">
      <!-- 左矢印 -->
      <button
        class="dashboard-banner-slider__arrow dashboard-banner-slider__arrow--prev"
        @click="prev">
        <img
          src="/img/common/icon-banner-slide-left-arrow.svg"
          alt="前へ" />
      </button>

      <!-- スライダー本体 -->
      <div class="dashboard-banner-slider__viewport">
        <div
          class="dashboard-banner-slider__track"
          :style="trackStyle">
          <div
            v-for="(slide, index) in normalizedSlides"
            :key="index"
            class="dashboard-banner-slider__slide"
            :class="{ 'is-active': index === currentIndex + 1 }">
            <!-- 画像スライド -->
            <template v-if="slide.type === 'image'">
              <a
                v-if="slide.link"
                :href="slide.link"
                :target="slide.target || '_self'"
                class="dashboard-banner-slider__link"
                :rel="slide.target === '_blank' ? 'noopener' : null">
                <img
                  :src="isMobile && slide.srcSp ? slide.srcSp : slide.src"
                  :alt="slide.alt"
                  class="dashboard-banner-slider__img"
                  loading="eager" />
              </a>
              <img
                v-else
                :src="isMobile && slide.srcSp ? slide.srcSp : slide.src"
                :alt="slide.alt"
                class="dashboard-banner-slider__img"
                loading="eager" />
            </template>
            <!-- コンポーネントスライド -->
            <component
              :is="slide.component"
              v-else-if="slide.type === 'component'" />
          </div>
        </div>
      </div>

      <!-- 右矢印 -->
      <button
        class="dashboard-banner-slider__arrow dashboard-banner-slider__arrow--next"
        @click="next">
        <img
          src="/img/common/icon-banner-slide-right-arrow.svg"
          alt="次へ"/>
      </button>
    </div>
    <!-- ドットナビゲーション -->
    <div class="dashboard-banner-slider__dots">
      <span
        v-for="(slide, index) in slides"
        :key="index"
        class="dashboard-banner-slider__dot"
        :class="{ 'is-active': index === (currentIndex + slides.length) % slides.length }"
        @click="goTo(index)" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardBannerSlider',
  components: {
    // NarasuBanner,
  },
  data() {
    return {
      currentIndex: 0,
      timer: null,
      autoPlay: true, // 自動再生
      autoPlayInterval: 5000,
      isTransitioning: false,
      isAnimating: false,
      windowWidth: 1120, // 画面幅
      realContainerWidth: 1120,
      realSlideWidth: 645,
      // -----------------------------------------------
      // スライドデータをここで管理する
      // -----------------------------------------------
      // type: 'image'
      //   src    : 画像パス（必須）
      //   srcSp  : スマホ用パス（任意：指定がない場合はsrcで補完）
      //   alt    : alt属性（必須）
      //   link   : リンク先URL（省略可）
      //   target : '_blank' など（省略時は '_self'）
      //
      // type: 'component'
      //   component : componentsに登録したコンポーネント名（必須）
      // -----------------------------------------------
      slides: [
        {
          type: 'image',
          src: '/img/narasu/submit-banner-sp-20260423.png',
          alt: 'narasuのサブミット機能サービス',
          link: '/service/submit/',
          target: '_blank',
        },
        {
          type: 'image',
          src: '/img/narasu/split-banner-sp-20260417.png',
          alt: 'narasuのスプリット機能サービス',
          link: '/service/split/',
          target: '_blank',
        },
        {
          type: 'image',
          src: '/img/narasu/copyright-banner-sp-20260421.png',
          alt: 'narasuの著作権管理サービス',
          link: '/service/license/',
          target: '_blank',
        },
      ],
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 896
    },
    // 循環構造のために最後のスライドを先頭に、最初のスライドを末尾に追加したもの
    normalizedSlides() {
      if (this.slides.length === 0) return []
      return [
        this.slides[this.slides.length - 1], // 末尾を先頭に
        ...this.slides,
        this.slides[0], // 先頭を末尾に
      ]
    },
    // アクティブスライドを中央に表示するためのオフセット
    // 645px（バナー幅）+ 20px（gap）= 665px ずつずらす
    trackStyle() {
      const bannerWidth = this.realSlideWidth || (this.isMobile ? this.windowWidth * 0.8 : 645)
      const gap = this.isMobile ? 12 : 20
      const containerWidth = this.realContainerWidth || (this.isMobile ? this.windowWidth : 1120)

      const offset = (containerWidth - bannerWidth) / 2
      const translateX = -((this.currentIndex + 1) * (bannerWidth + gap)) + offset

      return {
        transform: `translateX(${translateX}px)`,
        transition: this.isTransitioning ? 'transform 0.3s ease' : 'none',
      }
    },
  },
  mounted() {
    window.removeEventListener('resize', this.calculateWindowWidth)
    window.addEventListener('resize', this.calculateWindowWidth)
    this.calculateWindowWidth()

    if (this.autoPlay) {
      this.stopAutoPlay()
      this.startAutoPlay()
    }
  },
  beforeDestroy() {
    this.stopAutoPlay()
    window.removeEventListener('resize', this.calculateWindowWidth)
  },
  methods: {
    prev() {
      if (this.isAnimating) return

      this.isAnimating = true
      this.isTransitioning = true
      this.currentIndex -= 1

      setTimeout(() => {
        if (this.currentIndex < 0) {
          this.isTransitioning = false
          this.currentIndex = this.slides.length - 1
        }
        setTimeout(() => {
          this.isAnimating = false
        }, 20)
      }, 320)
    },

    next() {
      if (this.isAnimating) return

      this.isAnimating = true
      this.isTransitioning = true
      this.currentIndex += 1

      setTimeout(() => {
        if (this.currentIndex >= this.slides.length) {
          this.isTransitioning = false
          this.currentIndex = 0
        }

        setTimeout(() => {
          this.isAnimating = false
        }, 20)
      }, 320)
    },

    goTo(index) {
      // ロック中、または既にそのスライドにいる場合はキャンセル
      if (this.isAnimating || this.currentIndex === index) return

      this.isAnimating = true
      this.isTransitioning = true
      this.currentIndex = index

      setTimeout(() => {
        this.isAnimating = false
      }, 300)
    },

    startAutoPlay() {
      this.timer = setInterval(this.next, this.autoPlayInterval)
    },

    stopAutoPlay() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    calculateWindowWidth() {
      this.windowWidth = window.innerWidth

      // DOMの描画が更新された後に、実際のピクセル数を取得する
      this.$nextTick(() => {
        const viewport = this.$el.querySelector('.dashboard-banner-slider__viewport')
        const slide = this.$el.querySelector('.dashboard-banner-slider__slide')

        if (viewport && slide) {
          this.realContainerWidth = viewport.clientWidth
          this.realSlideWidth = slide.clientWidth
        }
      })
    },
  },
}
</script>

<style scoped>

</style>
